import {
  ChangeDetectionStrategy,
  Component,
  ChangeDetectorRef,
  ElementRef,
  EventEmitter,
  Input,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ProximityAlertMessage, SimulationSocketService, AdvocateLogMessage } from 'src/app/services';

export { AdvocateLogMessage };

export type ProximitySimulationOverlayZone = 'red' | 'yellow' | 'outside' | 'unknown';

@Component({
  selector: 'app-proximity-simulation-overlay',
  templateUrl: './proximity-simulation-overlay.component.html',
  styleUrls: ['./proximity-simulation-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProximitySimulationOverlayComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() distanceParameter = 'meter';
  @Input() isPerpetrator = false;
  @Input() threatPairId = '';
  @Input() title = 'Simulation mode';
  @Input() zone: ProximitySimulationOverlayZone = 'unknown';
  @Input() completedCheckIns = 0;
  @Input() totalCheckIns = 0;
  @Input() survivorName = 'Survivor';
  @Input() perpetratorName = 'Perpetrator';
  @Input() telemetryExpanded = false;
  @Input() socketUserIds: string[] = [];

  @Output() editSimulation = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  @ViewChild('eventLogList') private eventLogList?: ElementRef<HTMLOListElement>;
  eventLogs: AdvocateLogMessage[] = [];

  now = new Date();
  private survivorAlert: ProximityAlertMessage | undefined;
  private perpetratorAlert: ProximityAlertMessage | undefined;
  private readonly subscriptions = new Subscription();
  private survivorAlertTimer: ReturnType<typeof setTimeout> | undefined;
  private perpetratorAlertTimer: ReturnType<typeof setTimeout> | undefined;
  private logScrollHandle: ReturnType<typeof setTimeout> | undefined;
  private isSurvivorAlertDismissed = false;
  private isPerpetratorAlertDismissed = false;
  private clockHandle = setInterval(() => {
    this.now = new Date();
    this.changeDetector.markForCheck();
  }, 1000);

  constructor(
    private simulationSocketService: SimulationSocketService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const [survivorUserId, perpetratorUserId] = this.socketUserIds;

    const onProximityAlert = (userId: string, alert: ProximityAlertMessage): void => {
      if (userId === survivorUserId) {
        this.survivorAlert = alert;
      } else if (userId === perpetratorUserId) {
        this.perpetratorAlert = alert;
      }
      // Wake both watches on any proximity event (same as original UX),
      // but keep role-specific bodies so each face can show a different message.
      this.isSurvivorAlertDismissed = false;
      this.isPerpetratorAlertDismissed = false;
      this.startAlertTimer('survivor');
      this.startAlertTimer('perpetrator');
      this.changeDetector.markForCheck();
    };

    if (survivorUserId) {
      this.subscriptions.add(
        this.simulationSocketService.listenForProximityAlerts(survivorUserId).subscribe((alert) => {
          onProximityAlert(survivorUserId, alert);
        }),
      );
    }

    if (perpetratorUserId && perpetratorUserId !== survivorUserId) {
      this.subscriptions.add(
        this.simulationSocketService.listenForProximityAlerts(perpetratorUserId).subscribe((alert) => {
          onProximityAlert(perpetratorUserId, alert);
        }),
      );
    }

    this.subscriptions.add(
      this.simulationSocketService.listenForAdvocateLogs(this.threatPairId, this.distanceParameter).subscribe((log) => {
        this.eventLogs.unshift(log);
        //this.eventLog.splice(20);
        this.changeDetector.markForCheck();
        this.scrollEventLogToLatest();
      }),
    );
  }

  get isSurvivorAlerting(): boolean {
    return !this.isSurvivorAlertDismissed && (!!this.survivorAlert || !!this.perpetratorAlert);
  }

  get isPerpetratorAlerting(): boolean {
    return !this.isPerpetratorAlertDismissed && (!!this.perpetratorAlert || !!this.survivorAlert);
  }

  ngOnChanges(changes: Record<string, { currentValue: unknown; previousValue?: unknown }>): void {
    if (changes['eventLog']) {
      this.scrollEventLogToLatest();
    }
  }

  ngAfterViewInit(): void {
    this.scrollEventLogToLatest();
  }

  private scrollEventLogToLatest(): void {
    if (this.logScrollHandle) {
      clearTimeout(this.logScrollHandle);
    }
    this.logScrollHandle = setTimeout(() => {
      if (this.eventLogList) {
        this.eventLogList.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
      this.logScrollHandle = undefined;
    });
  }

  dismissAlert(watch: 'survivor' | 'perpetrator'): void {
    if (watch === 'survivor') {
      this.isSurvivorAlertDismissed = true;
    } else {
      this.isPerpetratorAlertDismissed = true;
    }
    this.changeDetector.markForCheck();
  }

  private startAlertTimer(watch: 'survivor' | 'perpetrator'): void {
    this.clearAlertTimer(watch);
    const timer = setTimeout(() => {
      if (watch === 'survivor') {
        this.isSurvivorAlertDismissed = true;
      } else {
        this.isPerpetratorAlertDismissed = true;
      }
      this.changeDetector.markForCheck();
    }, 8000);
    if (watch === 'survivor') {
      this.survivorAlertTimer = timer;
    } else {
      this.perpetratorAlertTimer = timer;
    }
  }

  private clearAlertTimer(watch?: 'survivor' | 'perpetrator'): void {
    if (!watch || watch === 'survivor') {
      if (this.survivorAlertTimer) {
        clearTimeout(this.survivorAlertTimer);
        this.survivorAlertTimer = undefined;
      }
    }
    if (!watch || watch === 'perpetrator') {
      if (this.perpetratorAlertTimer) {
        clearTimeout(this.perpetratorAlertTimer);
        this.perpetratorAlertTimer = undefined;
      }
    }
  }

  get survivorAlertTitle(): string {
    return this.survivorAlert?.title || this.perpetratorAlert?.title || 'Proximity alert';
  }

  get perpetratorAlertTitle(): string {
    return this.perpetratorAlert?.title || this.survivorAlert?.title || 'Proximity alert';
  }

  get survivorAlertBody(): string {
    // Prefer this role's socket body; otherwise use the role-specific fallback
    // (not the other party's body) so the two watches never mirror each other.
    return this.survivorAlert?.body || 'Alert: threat is closing in. Stay safe.';
  }

  get perpetratorAlertBody(): string {
    return this.perpetratorAlert?.body || 'Immediate alert: leave this area now.';
  }

  get logCount(): number {
    return this.eventLogs.length;
  }

  get progressPercent(): number {
    if (!this.totalCheckIns) {
      return 0;
    }
    return Math.min((this.completedCheckIns / this.totalCheckIns) * 100, 100);
  }

  trackLog(index: number): number {
    return index;
  }

  logBadgeClass(entry: AdvocateLogMessage): string {
    const value = this.logBadgeLabel(entry).toLowerCase();
    return value.includes('red') || value.includes('emergency')
      ? 'proximity-overlay__badge--emergency'
      : value.includes('yellow') || value.includes('warning')
        ? 'proximity-overlay__badge--warning'
        : 'proximity-overlay__badge--info';
  }

  logBadgeLabel(entry: AdvocateLogMessage): string {
    return entry.zone;
  }

  ngOnDestroy(): void {
    clearInterval(this.clockHandle);
    this.clearAlertTimer();
    if (this.logScrollHandle) {
      clearTimeout(this.logScrollHandle);
    }
    this.subscriptions.unsubscribe();
  }
}
